import i18n from '@/vue-app/plugins/i18n';
import TYPES from '@/types';
import Vue from 'vue';
import { getGoalIconPath } from '@/vue-app/utils/goal-icon-path';

// Application
import GetAssociatedProductQuery from '@/modules/flagship/investor-goal/associated-product/application/queries/get-associated-product-query';

// Domain
import {
  LinkGoalsWealthStateManager,
} from '@/modules/flagship/investor-goal/link-sowos-wealth/domain/state/link-goals-wealth-state-manager';
import {
  AssociatedProductEntity,
} from '@/modules/flagship/investor-goal/associated-product/domain/entities/associated-product-entity';
import Inject from '@/modules/shared/domain/di/inject';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';

type AssociatedProduct = {
  investor_goal_id: string;
  label: string;
  icon: string;
  icon_path: string;
  recommended_amount: number;
}

export default class StepChooseGoalsWealthViewModel {
  @Inject(TYPES.GET_ASSOCIATED_PRODUCT_QUERY)
  private readonly get_associated_product_query!: GetAssociatedProductQuery;

  @Inject(TYPES.LINK_GOALS_WEALTH_STATE_MANAGER)
  readonly link_goals_wealth_state_manager!: LinkGoalsWealthStateManager;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  private readonly view!: Vue;

  readonly i18n_namespace = 'components.flagship.flagship-link-goals.step-choose-goals-wealth';

  link_goals_wealth_state = this.link_goals_wealth_state_manager.state;

  associated_product_selected: Array<string> = [];

  associated_product: Array<AssociatedProduct> = [];

  public constructor(view: Vue) {
    this.view = view;
  }

  get disable_btn_continue() {
    return this.associated_product_selected.length === 0 || this.link_goals_wealth_state.is_loading;
  }

  translate = (value: string) => (
    i18n.t(`${this.i18n_namespace}.${value}`) as string
  )

  initialize = async () => {
    this.link_goals_wealth_state.is_loading = true;
    await this.getAssociatedProduct();
    this.link_goals_wealth_state.is_loading = false;
  }

  getIconMaxWidth = (icon_goal_name: string) => ((icon_goal_name === 'icon-add.svg') ? '28px' : '25px');

  addAssociatedProduct = (product: AssociatedProductEntity) => {
    this.associated_product.push({
      investor_goal_id: product.investor_goal_id,
      label: (product.custom_investor_goal) ? product.custom_investor_goal.goal_name
        : product.investment_goal_type.label,
      // eslint-disable-next-line no-nested-ternary
      icon: (product.custom_investor_goal)
        ? product.custom_investor_goal.custom_goal_type.icon_name
        : (product.investment_goal_type.name === 'retirement')
          ? 'icon-retirement.svg' : `icon-${product.investment_goal_type.name}.svg`,
      icon_path: getGoalIconPath(
        product.investor_goal_id,
        product.custom_investor_goal?.custom_goal_type?.icon_name || 'icon-retirement.svg',
        product.custom_investor_goal?.updated_at || '',
      ),
      recommended_amount: product.monthly_required_amount,
    });
  }

  getAssociatedProduct = async () => {
    try {
      this.link_goals_wealth_state.is_loading = true;
      const associated_products = await this.get_associated_product_query
        .execute(this.link_goals_wealth_state.associated_product_id);
      associated_products.forEach((product) => {
        if (!product.is_active) {
          this.addAssociatedProduct(product);
        }
      });
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.get_associated_product'));
    } finally {
      this.link_goals_wealth_state.is_loading = false;
    }
  };

  createInvestorGoalsDto = () => {
    this.link_goals_wealth_state.selected_goals.length = 0;
    this.associated_product_selected.forEach((investor_goal_id) => {
      this.associated_product.forEach((associated_product) => {
        if (associated_product.investor_goal_id === investor_goal_id) {
          this.link_goals_wealth_state.selected_goals.push({
            investor_goal_id: associated_product.investor_goal_id,
            label: associated_product.label,
            icon: associated_product.icon,
            icon_path: associated_product.icon_path,
            active_goal_amount: 0,
            active_goal_amount_field: '0',
            recommend_amount: associated_product.recommended_amount,
            recurrent_assigned_amount: 0,
            recurrent_assigned_amount_field: '0',
            message: '',
            class: '',
          });
        }
      });
    });
  }

  nextStep = () => {
    this.createInvestorGoalsDto();
    this.view.$emit('nextStep');
  }

  closeModal = () => {
    this.view.$emit('closeModal');
  }
}
